import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import tw, { styled } from "twin.macro"

import { brandRules } from "utils/brand"
import { insightsDate, insightsPath } from "utils/insights"
import Modal from "components/modal"
import YouTube from "components/youtube.js"
import PlayButton from "images/icons/play-button.svg"
import Tag from "components/tag"
import { sendViewEvent, sendClickEvent } from "utils/algolia"

export const Frame = styled.div`
  min-height: 507px;

  .cover {
    transition: all 0.15s ease-out;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover .cover {
      transform: scale(1.1);
    }
  }

  .shadow {
    box-shadow: 4px 4px 15px rgba(215, 215, 215, 0.5);
  }

  ais-highlight-0000000000 {
    color: #3498db;
  }

  &.with-press-logo {
    min-height: 577px;
  }
`

export const FeaturedImage = styled.div`
  clip-path: polygon(0% 0%, 100% 0%, 100% calc(100% - 15px), 0% 100%);
  margin: 0;
  margin-bottom: -30px;
  position: relative;
  padding: 0;
  ${"" /* Fix for clip-path bug on Chrome */}
  width: calc(100% + 0.5px);
  z-index: 1;

  &.swiper {
    width: 100%;
    display: flex;
  }

  .play-button {
    position: absolute;
    top: calc(50% - 40px);
    left: calc(50% - 40px);
    z-index: 2;
  }
`

const CoverImage = styled.div.attrs((props) => ({
  style: { backgroundImage: `url(${props.bg})` },
}))`
  height: 100%;
  width: 100%;
  z-index: 1;
  background-size: cover;
  background-position: center;
`

export const Title = styled.h3`
  font-size: 17px;
  line-height: 22px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const Metadata = styled.div`
  font-size: 11px;
  line-height: 13px;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Excerpt = styled.div`
  ${tw`font-body text-grey-5 my-1`}
  font-size: 13px;
  line-height: 22px;
  height: 108px;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;

  .understrap-read-more-link {
    display: none;
  }
`

const ReadMore = styled.div`
  font-size: 15px;
  padding-top: 24px;
`
const ReadMoreMark = styled.div`
  background: linear-gradient(180deg, #3498db 0%, #006aa9 100%);
  box-shadow: 0px 0px 4px rgba(0, 106, 169, 0.510298);
  display: inline-block;
  margin-right: 7px;
  height: 17px;
  width: 4px;
`

const PressLogo = styled.div`
  ${tw`w-full flex flex-row items-center justify-center rounded-t`}

  height: 70px;
  background: linear-gradient(91.74deg, #2d2d2d 0.91%, #0c0c0c 100.68%);

  img {
    height: 35px;
  }
`

const InsightsLink = ({
  to,
  external,
  children,
  className,
  video = false,
  hitInfo,
  ...rest
}) => {
  const [player, setPlayer] = useState(false)

  var re =
    /https?:\/\/(?:[0-9A-Z-]+\.)?(?:youtu\.be\/|youtube(?:-nocookie)?\.com\S*?[^\w\s-])([\w-]{11})(?=[^\w-]|$)(?![?=&+%\w.-]*(?:['"][^<>]*>|<\/a>))[?=&+%\w.-]*/gi
  const videoId = to ? to.replace(re, "$1") : null

  function onClick(e) {
    const articleType = external ? "External" : "Internal"
    const event = `${articleType} Article Click`

    sendClickEvent({
      index: process.env.GATSBY_ALGOLIA_INDEX_NAME,
      event,
      hitInfo,
    })

    if (external && video) {
      e.preventDefault()
      e.stopPropagation()
      setPlayer(true)
    }
  }

  function closePlayer(e) {
    setPlayer(false)
  }

  if (external) {
    const newWindow =
      to.substr(0, 36) !== "https://insights.parrotanalytics.com"
    return (
      <>
        <a
          className={`flex flex-col flex-grow flex-shrink-0 ${className}`}
          href={to}
          rel={newWindow ? "noopener noreferrer" : ""}
          target={newWindow ? "_blank" : ""}
          onClick={onClick}
          {...rest}
        >
          {children}
        </a>
        {video && (
          <Modal isVisible={player} onClose={closePlayer}>
            <div className="video-player h-full w-full m:px-16">
              <YouTube
                videoId={videoId}
                active={true}
                autoplay={true}
                fixAspectRatio={false}
              />
            </div>
          </Modal>
        )}
      </>
    )
  } else {
    return (
      <Link
        to={to}
        className={`flex flex-col flex-grow ${className}`}
        onClick={onClick}
        {...rest}
      >
        {children}
      </Link>
    )
  }
}

const InsightsCard = ({ post, wide, search, xl = false, swiper = false }) => {
  // @TODO simplify logic as we do not have Wordpress posts any more
  post.category = post.categories
    ? post.categories[0]
    : post.insightsCategories?.length
    ? post.insightsCategories[0].name
    : post.label || false

  post.date = post.publishingDate || post.date

  post.excerpt =
    post.excerpt ||
    (post.shortDescription?.html
      ? post.shortDescription.html
      : post.legacySummary
      ? post.legacySummary
      : "")

  if (
    !post.format &&
    post.category === "Videos" &&
    (post.externalUrl || post.youtubeId)
  ) {
    post.format = "Video"
  }

  if (post.format === "Video" && post.youtubeId && !post.externalUrl) {
    post.externalUrl = "https://www.youtube.com/watch?v=" + post.youtubeId
  }

  const external =
    post?.externalUrl != null &&
    post?.externalUrl?.trim() !== "" &&
    post?.externalUrl?.substr(0, 1) !== "/"

  const to = external
    ? post.externalUrl
    : post.path
    ? post.path
    : post.slug
    ? insightsPath(post)
    : post.youtubeId
    ? "https://www.youtube.com/watch?v=" + post.youtubeId
    : post.externalUrl

  const coverImage = post?.featuredImage?.url
    ? post.featuredImage.url
    : post.thumbnail?.url
    ? post.thumbnail.url
    : false

  const optimisedImgRaw =
    !xl && post?.featuredImage && post?.featuredImage?.fixed
      ? post.featuredImage.fixed.src
      : post?.thumbnail && post?.thumbnail?.fixed
      ? post.thumbnail.fixed.src
      : false

  const optimisedImg = optimisedImgRaw
      ? optimisedImgRaw.replace("no_metadata", "compress=metadata:false").replace("/compress/", "/")
      : false;

  const imageURL = coverImage || ""

  const hitInfo = post.hitInfo

  const pressLogoUrl = post?.pressLogo?.url

  useEffect(() => {
    sendViewEvent({
      index: process.env.GATSBY_ALGOLIA_INDEX_NAME,
      event: "Article Card View",
      hitInfo,
    })
  }, [])

  return (
    <Frame
      className={`w-full flex flex-col flex-grow-0
        ${!swiper ? "my-2 px-2" : ""}
        ${xl === true ? "l:w-full" : "m:w-1/2"}
        ${wide === true ? "l:w-1/3" : "l:w-1/4"}
        ${pressLogoUrl ? "with-press-logo" : ""}
      `}
      xl={xl}
    >
      <InsightsLink
        to={to}
        external={external}
        video={post.format === "Video"}
        className={xl ? "shadow" : ""}
        hitInfo={hitInfo}
      >
        {pressLogoUrl && (
          <PressLogo>
            <img src={pressLogoUrl} alt="" />
          </PressLogo>
        )}
        {(!!optimisedImg || !!coverImage) && (
          <FeaturedImage
            className={`bg-grey-15 flex flex-col flex-grow overflow-hidden ${
              swiper ? "swiper" : ""
            }`}
          >
            {!xl && !!optimisedImg && (
              <img
                src={optimisedImg}
                className="flex flex-grow object-cover cover"
              />
            )}
            {(!optimisedImg || xl) && !!coverImage && (
              <CoverImage bg={imageURL} className="flex flex-grow cover" />
            )}
            {post.format === "Video" && (
              <img className="play-button" src={PlayButton} alt="play video" />
            )}
          </FeaturedImage>
        )}
        <div
          className={`p-4 bg-white rounded font-body flex flex-col items-start ${
            coverImage ? "flex-shrink" : "flex-grow"
          }`}
        >
          {post.category && (
            <Tag className="red z-10 mb-2">{post.category}</Tag>
          )}
          <Title
            className={`font-display text-lg text-grey-10 my-1 ${
              post.category ? "" : "mt-6"
            }`}
            dangerouslySetInnerHTML={{ __html: brandRules(post.title) }}
          ></Title>
          {!!post.date && (
            <Metadata className="font-light text-grey-45 my-2">
              {insightsDate(post.date)}
            </Metadata>
          )}
          <div className="flex-grow">
            {(!xl || swiper) && (
              <Excerpt
                dangerouslySetInnerHTML={{
                  __html: `<span>${brandRules(post.excerpt)}</span>`,
                }}
              />
            )}
          </div>
          <ReadMore className="uppercase font-display text-grey-35 flex flex-row items-top">
            <ReadMoreMark />
            {!post.linkCaption && (
              <>{post?.format === "Video" ? "Watch Now" : "Read More"}</>
            )}
            {!!post.linkCaption && post.linkCaption}
          </ReadMore>
        </div>
      </InsightsLink>
    </Frame>
  )
}

InsightsCard.propTypes = {
  post: PropTypes.object.isRequired,
}

export default InsightsCard
